import SixDigitCodeInput from "@via/components/code-input/six-digit-code-input.tsx";
import AuthenticatorQr from "@via/pages/authentication/mfa/authenticator-qr.tsx";
import {
  mfaErrorMessage,
  useMfaEnrollConfirm,
  useMfaEnrollStart,
} from "@via/pages/authentication/mfa/mfa.api.tsx";
import { ChallengeGone } from "@via/pages/authentication/mfa/mfa-notices.tsx";
import type { MfaSessionResponse } from "common";
import { type JSX, useState } from "react";

/**
 * First time setup at sign-in. After an administrator reset the challenge needs the
 * one-time code they handed over, on both the start and the confirm call.
 */
export default function MfaEnrollScreen({
  needsHandoff,
  onSession,
}: {
  needsHandoff: boolean;
  onSession: (response: MfaSessionResponse) => void;
}): JSX.Element {
  const start = useMfaEnrollStart();
  const confirm = useMfaEnrollConfirm();
  const [handoffCode, setHandoffCode] = useState("");
  const [attempt, setAttempt] = useState(0);
  const startError = start.isError ? mfaErrorMessage(start.error) : null;
  const confirmError = confirm.isError ? mfaErrorMessage(confirm.error) : null;
  const handoff = needsHandoff ? { handoffCode: handoffCode.trim() } : {};

  if (
    startError?.code === "challenge_invalid" ||
    confirmError?.code === "challenge_invalid"
  ) {
    return <ChallengeGone />;
  }

  if (!start.data) {
    return (
      <form
        className="space-y-4"
        onSubmit={e => {
          e.preventDefault();
          start.mutate(handoff);
        }}>
        <h2 className="via-h2 text-center text-2xl font-bold">
          {needsHandoff
            ? "Set up your authenticator again"
            : "Set up multi-factor sign-in"}
        </h2>
        <p className="text-sm text-gray-600">
          {needsHandoff
            ? "An administrator reset multi-factor sign-in for your account. Enter the one-time code they gave you to continue."
            : "Signing in to Foundry Connect needs a code from an authenticator app. Install Duo Mobile, Google Authenticator, Microsoft Authenticator or any other authenticator app on your phone, then continue."}
        </p>
        {needsHandoff && (
          <div>
            <label
              htmlFor="mfa-handoff-code"
              className="block text-sm font-medium leading-6 text-gray-900">
              One-time code from your administrator
            </label>
            <input
              id="mfa-handoff-code"
              autoComplete="off"
              value={handoffCode}
              onChange={e => setHandoffCode(e.target.value)}
              placeholder="xxxxx-xxxxx-xxxxx"
              className="mt-2 block w-full rounded-md border-0 py-1.5 font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        )}
        {startError && (
          <p role="alert" className="text-sm font-semibold text-rose-600">
            {startError.message}
          </p>
        )}
        <button
          type="submit"
          disabled={
            start.isPending || (needsHandoff && handoffCode.trim().length < 15)
          }
          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
          Continue
        </button>
      </form>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="via-h2 text-center text-2xl font-bold">
        Scan this QR code
      </h2>
      <p className="text-sm text-gray-600">
        In your authenticator app, add an account and scan this code. In Duo
        Mobile choose Add, then Use QR code; in Google Authenticator or
        Microsoft Authenticator choose Add account.
      </p>
      <AuthenticatorQr setup={start.data} />
      <p className="text-center text-sm text-gray-600">
        Then enter the 6 digit code the app shows.
      </p>
      <SixDigitCodeInput
        resetKey={attempt}
        disabled={confirm.isPending}
        onComplete={code =>
          confirm.mutate(
            { code, ...handoff },
            { onSuccess: onSession, onError: () => setAttempt(a => a + 1) }
          )
        }
      />
      {confirmError && (
        <p
          role="alert"
          className="text-center text-sm font-semibold text-rose-600">
          {confirmError.message}
        </p>
      )}
    </div>
  );
}
